import { Hooks, MapSpecModifiers } from '@platform/maplibre';
import { Buttons, Dropdowns } from '@platform/shared/ui';
import { ProjectTypes } from '@platform/types';
import crc32 from 'crc/crc32';
import produce from 'immer';
import { GeoJSONFeature, Map } from 'maplibre-gl';
import React, { useEffect, useMemo } from 'react';
import ExpandableContainer from '../../shared/ExpandableContainer';
import AnalysisPanelSelectionMainMap from './AnalysisPanelSelectionMainMap';

const { useFeaturesUnderMouse, useFeatureHighlightSingleMap } = Hooks;

const ANALYSIS_DATA_SOURCE = `ANALYSIS:source`;
const ANALYSIS_FILL_LAYER_ID = `ANALYSIS:fill"`;
const ANALYSIS_LINE_LAYER_ID = `ANALYSIS:line`;

type SelectionLayer = {
  name: string;
  datasetId: string;
  geoLayerId: string;
  rowIdentifier: string;
  nameIdentifier: string;
  summaryLevel: string;
};

// TODO:
const AVAILABLE_LAYERS: SelectionLayer[] = [
  {
    name: 'States (ACS 2020 5yr)',
    datasetId: '365480',
    geoLayerId: '316249',
    rowIdentifier: 'Geo_FIPS', // '__ID__',
    nameIdentifier: 'Geo_QName',
    summaryLevel: 'SL040',
  },
  {
    name: 'Counties (ACS 2020 5yr)',
    datasetId: '365481',
    geoLayerId: '316307',
    rowIdentifier: '__ID__',
    nameIdentifier: 'Geo_QName',
    summaryLevel: 'SL050',
  },
  {
    name: 'States (1810)',
    datasetId: '0',
    geoLayerId: '767',
    rowIdentifier: '__ID__',
    nameIdentifier: 'Geo_QNAME',
    summaryLevel: 'SL040',
  },
];

interface Props {
  map: Map;
  styleSpec: ProjectTypes.MapSpec;
  onStyleUpdate: (newMapStyle: ProjectTypes.MapSpec) => void;
}

const AnalysisPanelSelectionTools: React.FC<Props> = ({ map, styleSpec, onStyleUpdate }) => {
  const { workingLayers, analysis } = styleSpec;

  const cleanUpMap = () => {
    if (map.getLayer(ANALYSIS_FILL_LAYER_ID)) {
      map.removeLayer(ANALYSIS_FILL_LAYER_ID);
    }

    if (map.getLayer(ANALYSIS_LINE_LAYER_ID)) {
      map.removeLayer(ANALYSIS_LINE_LAYER_ID);
    }

    if (map.getSource(ANALYSIS_DATA_SOURCE)) {
      map?.removeSource(ANALYSIS_DATA_SOURCE);
    }
  };

  useEffect(() => {
    cleanUpMap();

    if (analysis?.layer) {
      const { datasetId, rowIdentifier, nameIdentifier, geoLayerId } = analysis.layer;

      map.addSource(ANALYSIS_DATA_SOURCE, {
        type: 'vector',
        tiles: [
          `${process.env.NX_SOCEX_TILES_SERVER}?x={x}&y={y}&z={z}&layers=${geoLayerId}&columns=${geoLayerId}.${datasetId}.${rowIdentifier},${geoLayerId}.${datasetId}.${nameIdentifier}&projection=EPSG-3857`,
        ],
      });

      map.addLayer({
        id: ANALYSIS_FILL_LAYER_ID,
        source: ANALYSIS_DATA_SOURCE,
        'source-layer': geoLayerId,
        layout: {
          visibility: 'visible',
        },
        type: 'fill',
        paint: {
          'fill-color': ['case', ['boolean', ['feature-state', 'hover'], false], 'rgb(255, 70, 29)', 'red'],
          'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.65, 0],
          'fill-outline-color': 'rgba(125,125,125,1)',
        },
      });

      map.addLayer({
        id: ANALYSIS_LINE_LAYER_ID,
        source: ANALYSIS_DATA_SOURCE,
        'source-layer': geoLayerId,
        layout: {
          visibility: 'visible',
        },
        paint: {
          'line-color': 'rgb(125,125,125)',
          'line-opacity': 0.85,
          'line-width': 1,
        },
        type: 'line',
      });
    }
    return () => {
      cleanUpMap();
    };
  }, [analysis?.layer]);

  const handleSelectionLayerFeaturesUnderMouse = (featuresInEvent: GeoJSONFeature[]) => {
    if (!analysis) return;

    const { layer } = analysis;
    const { rowIdentifier, nameIdentifier } = layer;
    const newSelection = [...analysis.selection] ?? [];

    featuresInEvent.forEach((f) => {
      const idx = newSelection.findIndex((x) => x.id === f.properties[rowIdentifier]);
      if (idx > -1) {
        newSelection.splice(idx, 1);
      } else {
        newSelection.push({ id: f.properties[rowIdentifier], label: f.properties[nameIdentifier] });
      }
    });

    const updatedMapSpec = produce(styleSpec, (draft) => {
      if (draft.analysis) {
        draft.analysis.selection = newSelection;
      }
    });

    onStyleUpdate(updatedMapSpec);
  };

  const sourceLayerIds = useMemo(() => (analysis ? [`${ANALYSIS_FILL_LAYER_ID}`] : []), [analysis]);

  useFeatureHighlightSingleMap(map, sourceLayerIds);
  useFeaturesUnderMouse(map, 'click', handleSelectionLayerFeaturesUnderMouse, sourceLayerIds);

  const availableSelectionLayers: Dropdowns.DropdownOption[] = AVAILABLE_LAYERS.map((x) => ({
    name: x.name,
    value: x.geoLayerId,
  }));

  const handleSelectionLayerChange = (opt: Dropdowns.DropdownOption) => {
    const selectionLayer = AVAILABLE_LAYERS.find((x) => x.geoLayerId === opt.value);
    if (!selectionLayer) return;

    const updatedMapSpec = produce(styleSpec, (draft) => {
      draft.analysis = {
        fingerPrint: getAnalysisFingerPrint(styleSpec),
        layer: {
          datasetId: selectionLayer.datasetId,
          geoLayerId: selectionLayer.geoLayerId,
          nameIdentifier: selectionLayer.nameIdentifier,
          rowIdentifier: selectionLayer.rowIdentifier,
          summaryLevel: selectionLayer.summaryLevel,
        },
        selection: [],
        type: 'geo',
      };
    });
    onStyleUpdate(updatedMapSpec);
  };

  const handleResetSelection = () => {
    const updatedMapSpec = produce(styleSpec, (draft) => {
      if (draft.analysis) {
        draft.analysis = undefined;
      }
      draft.workingLayers.forEach((wLayer) => (wLayer.featureSelection = []));
    });
    onStyleUpdate(updatedMapSpec);
  };

  const handleFeatureSelection = (
    featureSelectionPerLayer: Record<string, ProjectTypes.WorkingLayerFeatureSelection[]>
  ) => {
    let updatedMapSpec = { ...styleSpec };

    styleSpec.workingLayers.forEach((workingLayer) => {
      const updatedWorkingLayer = produce(workingLayer, (draft) => {
        draft.featureSelection = featureSelectionPerLayer[workingLayer.id] ?? [];
      });
      updatedMapSpec = MapSpecModifiers.updateWorkingLayer(updatedMapSpec, updatedWorkingLayer);
    });
    onStyleUpdate(updatedMapSpec);
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            Select by:
            <Dropdowns.Dropdown
              placeholder="Select one..."
              options={availableSelectionLayers}
              value={analysis?.layer.geoLayerId}
              onSelect={handleSelectionLayerChange}
            />
          </div>
        </div>
        {analysis && analysis.selection.length > 0 && (
          <div className="flex flex-col gap-1">
            <div className="flex gap-2">
              <Buttons.Soft onClick={handleResetSelection}>Clear Selection</Buttons.Soft>
            </div>
            <ExpandableContainer hasToggle={false} title="Selection" hint={`${analysis.selection.length} selected`}>
              <div className="flex flex-wrap gap-1">
                {analysis.selection.map((x) => (
                  <div key={x.id} className="rounded-md bg-gray-200 p-1">
                    {x.label}
                  </div>
                ))}
              </div>
            </ExpandableContainer>
          </div>
        )}
      </div>
      <div className="-mx-6" />
      {analysis && (
        <AnalysisPanelSelectionMainMap
          analysis={analysis}
          workingLayers={workingLayers}
          onSelect={handleFeatureSelection}
        />
      )}
    </>
  );
};

export default AnalysisPanelSelectionTools;

const getAnalysisFingerPrint = (styleSpec: ProjectTypes.MapSpec) =>
  crc32(styleSpec.workingLayers.map((x) => JSON.stringify(x.metadata)).join());
